import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import { MOCK_SERVICE_ACCOUNTS } from "@/data/mock";
import { PermissionGate } from "@/components/permission/permission-gate";
import type { ServiceAccountRisk, ServiceAccountStatus } from "@/types";

/* USR-05 — Service Account Governance.
   Canonical source: Figma IAM-07 / prototype/pages/admin/service-accounts.html /
   identity-access-wireframes.html#IAM-07. `MOCK_SERVICE_ACCOUNTS` (already
   the live data backing this exact ID/name/owner/purpose/scopes/expiry/
   last-used/risk shape, plus a `status` field the static prototype table
   doesn't even show) is used as canonical, matching its own purpose-built
   Identity & Access typing. Per this task's explicit instruction — "Do NOT
   implement actual secret generation, credential issuance... Never
   display fabricated secrets... If the source only requires a governance/
   read-only view, keep it read-only" — this screen is strictly read-only:
   no per-row rotate/revoke actions were invented, and "+ Create Service
   Account" is an honest deferral (real credential issuance requires a
   secrets-management/HSM backend that does not exist here), not a fake
   client-side creation flow (unlike Groups, creating a service account
   fundamentally implies issuing new secret material). */

const RISK_TONE: Record<ServiceAccountRisk, "success" | "warning" | "danger"> = {
  Low: "success",
  Medium: "warning",
  High: "danger",
};

const STATUS_TONE: Record<ServiceAccountStatus, "success" | "warning" | "danger"> = {
  Healthy: "success",
  "Key Expired": "warning",
  "Auth Failing": "danger",
};

export function ServiceAccountGovernance() {
  const [query, setQuery] = useState("");

  const stats = useMemo(() => {
    const total = MOCK_SERVICE_ACCOUNTS.length;
    const healthy = MOCK_SERVICE_ACCOUNTS.filter((s) => s.status === "Healthy").length;
    const needsAttention = MOCK_SERVICE_ACCOUNTS.filter((s) => s.status !== "Healthy").length;
    const highRisk = MOCK_SERVICE_ACCOUNTS.filter((s) => s.risk === "High").length;
    return { total, healthy, needsAttention, highRisk };
  }, []);

  const rows = useMemo(
    () =>
      MOCK_SERVICE_ACCOUNTS.filter((s) => {
        const q = query.toLowerCase();
        return !q || `${s.id} ${s.name} ${s.owner} ${s.purpose}`.toLowerCase().includes(q);
      }),
    [query]
  );

  const deferCreate = () =>
    toast({
      variant: "info",
      title: "Create Service Account — not implemented",
      description: "Issuing a new service account requires a real secrets-management/HSM backend to generate and store credentials. No account was created; no secret was generated.",
    });

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Service Accounts</span>
      </nav>

      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Service Account Governance</h1>
        <p className="mt-1 text-sm text-muted">Read-only view of machine identities, scopes, and credential health.</p>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-[10px] border border-line bg-surface px-[18px] py-[16px]">
          <div className="text-[24px] font-bold leading-[1.1] tracking-tight text-ink">{stats.total}</div>
          <div className="mt-0.5 text-[12px] font-medium text-muted">Service Accounts</div>
        </div>
        <div className="rounded-[10px] border border-line bg-surface px-[18px] py-[16px]">
          <div className="text-[24px] font-bold leading-[1.1] tracking-tight text-success">{stats.healthy}</div>
          <div className="mt-0.5 text-[12px] font-medium text-muted">Healthy</div>
        </div>
        <div className="rounded-[10px] border border-line bg-surface px-[18px] py-[16px]">
          <div className="text-[24px] font-bold leading-[1.1] tracking-tight text-warning">{stats.needsAttention}</div>
          <div className="mt-0.5 text-[12px] font-medium text-muted">Needs Attention</div>
        </div>
        <div className="rounded-[10px] border border-line bg-surface px-[18px] py-[16px]">
          <div className="text-[24px] font-bold leading-[1.1] tracking-tight text-danger">{stats.highRisk}</div>
          <div className="mt-0.5 text-[12px] font-medium text-muted">High Risk</div>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search service accounts..."
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        <PermissionGate permission="service-account:manage">
          <Button className="ml-auto rounded-[8px]" onClick={deferCreate}>+ Create Service Account</Button>
        </PermissionGate>
      </div>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">ID</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Name</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Owner</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Purpose</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Scopes</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Expiry</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Last Used</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Status</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-ink">{s.id}</td>
                <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] font-semibold text-ink">{s.name}</td>
                <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-muted">{s.owner}</td>
                <td className="px-4 py-[9px] text-[11.5px] text-ink">{s.purpose}</td>
                <td className="px-4 py-[9px]">
                  <div className="flex flex-wrap gap-1">
                    {s.scopes.map((sc) => (
                      <Badge key={sc} variant="neutral" size="sm" dot={false}>{sc}</Badge>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-muted">{s.expiry}</td>
                <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-muted">{s.lastUsed}</td>
                <td className="px-4 py-[9px]"><Badge variant={STATUS_TONE[s.status]} size="sm">{s.status}</Badge></td>
                <td className="px-4 py-[9px]"><Badge variant={RISK_TONE[s.risk]} size="sm">{s.risk}</Badge></td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted">No service accounts match your search.</div>
        )}
      </TableWrapper>
    </div>
  );
}
